import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    FiActivity,
    FiAlertTriangle,
    FiChevronDown,
    FiChevronRight,
    FiRefreshCw,
    FiSearch,
} from 'react-icons/fi';
import ReadinessCheckRow from './ReadinessCheckRow';
import {
    Tenant,
    TenantReadinessResult,
    SingleCheckResponse,
    getTenantReadiness,
    getCachedTenantReadiness,
} from '../../api/tenantApi';

const Spinner: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={`animate-spin ${className ?? 'h-3 w-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

const formatTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString();
};

const errorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.error
    ?? err?.response?.data?.message
    ?? err?.message
    ?? fallback;

/** "Not ready — 1 failing, 1 blocked." — built only from the counts that are non-zero. */
const buildFooterSummary = (result: TenantReadinessResult): string => {
    const s = result.summary;
    const parts: string[] = [];
    if (s.fail)    parts.push(`${s.fail} failing`);
    if (s.blocked) parts.push(`${s.blocked} blocked`);
    if (s.warn)    parts.push(`${s.warn} advisory warning${s.warn === 1 ? '' : 's'}`);
    if (s.unknown) parts.push(`${s.unknown} unknown`);
    if (result.canActivate) {
        return parts.length
            ? `Ready to activate — ${parts.join(', ')} (none of them required).`
            : 'Ready to activate — every required check is OK.';
    }
    return parts.length
        ? `Not ready — ${parts.join(', ')}.`
        : 'Not ready.';
};

interface TenantReadinessPanelProps {
    /** Mongo _id or Keycloak tenantId — the endpoint accepts either. */
    tenantId: string;
    /** The tenant already loaded by the page; used for the pending-restart ledger banner. */
    tenant?: Tenant | null;
}

/**
 * Persistent per-tenant readiness panel (plan of record §5.2). PROBES ONLY — this phase
 * ships no remediation and no activation. It supersedes the older Diagnostics block below
 * it, which is left working untouched.
 */
const TenantReadinessPanel: React.FC<TenantReadinessPanelProps> = ({ tenantId, tenant }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [result, setResult] = useState<TenantReadinessResult | null>(null);
    const [cachedAt, setCachedAt] = useState<string | null>(null);
    const [showingCached, setShowingCached] = useState(false);
    const [neverChecked, setNeverChecked] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [deepRefreshing, setDeepRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    const runRefresh = useCallback(async (deep: boolean) => {
        if (!tenantId) return;
        if (deep) setDeepRefreshing(true); else setRefreshing(true);
        setError(null);
        try {
            const response = await getTenantReadiness(tenantId, { deep });
            if (!alive.current) return;
            // A successful aggregation is ALWAYS 200, however red the checks are.
            setResult(response.data);
            setCachedAt(response.data?.checkedAt ?? null);
            setShowingCached(false);
            setNeverChecked(false);
        } catch (err: any) {
            if (!alive.current) return;
            setError(errorMessage(err, 'Readiness check failed.'));
        } finally {
            if (!alive.current) return;
            if (deep) setDeepRefreshing(false); else setRefreshing(false);
        }
    }, [tenantId]);

    // Paint from the cache first (no probing at all), then refresh behind it and swap in
    // the live result. The panel is therefore never blank and never blocks on ~1 s of probes.
    useEffect(() => {
        if (!tenantId) return;
        let cancelled = false;
        (async () => {
            try {
                const cached = await getCachedTenantReadiness(tenantId);
                if (cancelled || !alive.current) return;
                if (cached.data) {
                    setResult(cached.data);
                    setCachedAt(cached.cachedAt ?? cached.data.checkedAt ?? null);
                    setShowingCached(true);
                    setNeverChecked(false);
                } else {
                    setNeverChecked(true);
                }
            } catch {
                if (cancelled || !alive.current) return;
                // A missing cache must not stop the live refresh below — only a 404 is fatal,
                // and the refresh will surface that itself.
                setNeverChecked(true);
            }
            if (cancelled || !alive.current) return;
            await runRefresh(false);
        })();
        return () => { cancelled = true; };
    }, [tenantId, runRefresh]);

    const handleRowResult = useCallback((response: SingleCheckResponse) => {
        if (!alive.current) return;
        // The single-check endpoint recomputes summary/canActivate/blocking over the merged
        // set, so swapping in `data` updates the one row AND keeps the footer truthful.
        setResult(response.data);
        setCachedAt(response.data?.checkedAt ?? null);
        setShowingCached(false);
        setNeverChecked(false);
        setError(null);
    }, []);

    const handleRowError = useCallback((message: string) => {
        if (!alive.current) return;
        setError(message);
    }, []);

    const busy = refreshing || deepRefreshing;

    const labelsById: Record<string, string> = {};
    (result?.checks ?? []).forEach(c => { labelsById[c.id] = c.label; });

    // ── Pending rolling restart ─────────────────────────────────────────────
    // Phase 1 writes a terminal `namespace-restart` step (status 'pending') into the
    // provisioning ledger. `runtimeVisibility` failing is the same fact, probed live.
    // No amount of green elsewhere makes the tenant reachable until the namespace rolls.
    const pendingRestartStep = (tenant?.provisioning?.steps ?? [])
        .find(s => s.status === 'pending' && /restart/i.test(`${s.id} ${s.name}`));
    const runtimeCheck = (result?.checks ?? []).find(c => c.id === 'runtimeVisibility');
    const runtimeNotVisible = runtimeCheck?.status === 'fail';
    const showRestartBanner = Boolean(pendingRestartStep) || runtimeNotVisible;

    const activateTitle = result
        ? (result.canActivate
            ? 'Activation is not wired in this phase — the readiness panel is probes only.'
            : [
                'Cannot activate — required checks are not OK:',
                ...result.blockingChecks.map(b => `• ${labelsById[b.id] ?? b.id}: ${b.reason}`),
                '',
                'Activation is not wired in this phase — the readiness panel is probes only.',
              ].join('\n'))
        : 'Readiness has not been determined yet.';

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <button
                    type="button"
                    onClick={() => setCollapsed(c => !c)}
                    aria-expanded={!collapsed}
                    className="flex items-center gap-2 text-left">
                    {collapsed ? <FiChevronRight className="h-4 w-4 text-gray-400" /> : <FiChevronDown className="h-4 w-4 text-gray-400" />}
                    <FiActivity className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tenant readiness</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">— live probes, no changes are made</span>
                </button>

                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {showingCached ? 'cached from ' : 'last checked '}{formatTime(cachedAt)}
                    </span>
                    <button
                        type="button"
                        onClick={() => runRefresh(false)}
                        disabled={busy}
                        title="Re-run every check except the slow Keycloak artifact reconcile"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                        {refreshing ? <Spinner /> : <FiRefreshCw className="h-3 w-3" />}
                        Re-check all
                    </button>
                    <button
                        type="button"
                        onClick={() => runRefresh(true)}
                        disabled={busy}
                        title="Additionally runs the Keycloak artifact reconcile check — 1 to 3 seconds. Never runs on page load."
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50">
                        {deepRefreshing ? <Spinner /> : <FiSearch className="h-3 w-3" />}
                        Deep check <span className="font-normal opacity-70">(slow, ~1–3 s)</span>
                    </button>
                </div>
            </div>

            {!collapsed && (
                <>
                    {/* Pending rolling restart — persistent, and above everything else. */}
                    {showRestartBanner && (
                        <div className="mx-4 mt-3 flex items-start gap-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                            <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                                <div className="font-medium">Rolling restart pending — the tenant is not reachable yet.</div>
                                <div className="mt-0.5">
                                    {pendingRestartStep?.name ?? 'The namespace has not been restarted since this tenant was provisioned.'}
                                    {' '}Tenant configuration is read once at boot from a singleton
                                    {' '}<span className="font-mono">IOptions&lt;&gt;</span>, so no amount of green below makes the
                                    tenant reachable until the namespace has rolled. AppManagement does not trigger the restart.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Panel-level error — never a blank panel, never a thrown boundary. */}
                    {error && (
                        <div className="mx-4 mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    {/* Rows */}
                    <div className="mt-2">
                        {result && result.checks.length > 0 ? (
                            result.checks.map(check => (
                                <ReadinessCheckRow
                                    key={check.id}
                                    tenantId={tenantId}
                                    check={check}
                                    labelsById={labelsById}
                                    onResult={handleRowResult}
                                    onError={handleRowError}
                                    busy={busy}
                                />
                            ))
                        ) : (
                            <div className="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                                {busy
                                    ? 'Running the readiness checks…'
                                    : neverChecked
                                        ? 'This tenant has never been checked. Use “Re-check all” to run the probes.'
                                        : error
                                            ? 'No readiness result could be loaded.'
                                            : 'No readiness result yet.'}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {result && (
                        <div className="flex items-start justify-between gap-4 flex-wrap border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                            <div className="min-w-0">
                                <div className={`text-xs font-medium ${result.canActivate ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {buildFooterSummary(result)}
                                </div>
                                {result.blockingChecks.length > 0 && (
                                    <ul className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                                        {result.blockingChecks.map(b => (
                                            <li key={b.id}>
                                                <span className="font-medium">{labelsById[b.id] ?? b.id}</span>: {b.reason}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {!result.deep && (
                                    <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                                        Keycloak artifact completeness was not probed — run “Deep check” to include it.
                                    </div>
                                )}
                            </div>
                            {/*
                              Activation is the provisioning -> active flip, and it is NOT wired in
                              this phase. The button is rendered permanently disabled so the target
                              state is legible, with the blocking reasons in its tooltip.
                            */}
                            <button
                                type="button"
                                disabled
                                title={activateTitle}
                                className="inline-flex shrink-0 items-center gap-1.5 rounded bg-gray-300 dark:bg-gray-700 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 cursor-not-allowed">
                                Activate tenant
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TenantReadinessPanel;
